import { PathResponse } from "@repo/shared-types";
import { point, lineString } from "@turf/helpers";
import { nearestPointOnLine } from "@turf/nearest-point-on-line";
import { distance } from "@turf/distance";
import { length } from "@turf/length";
import { lineSlice } from "@turf/line-slice";

type PathWithGeoJson = Pick<PathResponse, "geoJson">;

export function calculatePathDistanceToCoordinates(
  path: PathWithGeoJson,
  coordinates: { lng: number; lat: number }
) {
  if (!path.geoJson) {
    throw new Error("path.geoJson is undefined");
  }

  try {
    const feature = path.geoJson.features[0];

    const multiLineCoords = feature.geometry.coordinates as unknown as [
      number,
      number,
    ][][];
    const flatLineCoords = multiLineCoords.flat();

    const line = lineString(flatLineCoords);
    const hoverPoint = point([coordinates.lng, coordinates.lat]);

    const snapped = nearestPointOnLine(line, hoverPoint);
    const snappedPoint = point(snapped.geometry.coordinates);
    const snappedIndex = snapped.properties.index;

    if (
      snappedIndex === undefined ||
      snappedIndex < 0 ||
      snappedIndex >= flatLineCoords.length
    ) {
      throw new Error("Snapped index is invalid");
    }

    const distanceBetweenHoverAndSnapped = distance(hoverPoint, snappedPoint);

    const startPoint = point(flatLineCoords[0]);
    const endPoint = point(flatLineCoords[flatLineCoords.length - 1]);

    const lineSliceToSnapped = lineSlice(startPoint, snappedPoint, line);
    const lineSliceFromSnapped = lineSlice(snappedPoint, endPoint, line);

    const distanceFromStart =
      length(lineSliceToSnapped) + distanceBetweenHoverAndSnapped;
    const distanceFromEnd =
      length(lineSliceFromSnapped) - distanceBetweenHoverAndSnapped;

    return { distanceFromStart, distanceFromEnd };
  } catch {
    const feature = path.geoJson.features[0];
    const lineCoords = feature.geometry.coordinates as [number, number][];

    const hoverPoint = point([coordinates.lng, coordinates.lat]);
    const snapped = nearestPointOnLine(feature, hoverPoint);
    const snappedIndex = snapped.properties.index;

    if (
      snappedIndex === undefined ||
      snappedIndex < 0 ||
      snappedIndex >= lineCoords.length
    ) {
      throw new Error("Snapped index is invalid");
    }

    const snappedPoint = point(snapped.geometry.coordinates);

    const distanceBetweenHoverAndSnapped = distance(hoverPoint, snappedPoint);

    const startPoint = point(lineCoords[0]);
    const endPoint = point(lineCoords[lineCoords.length - 1]);

    const lineSliceToSnapped = lineSlice(startPoint, snappedPoint, feature);
    const lineSliceFromSnapped = lineSlice(snappedPoint, endPoint, feature);

    const distanceFromStart =
      length(lineSliceToSnapped) + distanceBetweenHoverAndSnapped;
    const distanceFromEnd =
      length(lineSliceFromSnapped) - distanceBetweenHoverAndSnapped;

    return { distanceFromStart, distanceFromEnd };
  }
}
